import React, { useState } from 'react';
import { ORGANIZATION_ADMIN_MENU } from 'works/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import { SUPPLIER_ADMIN_MENU } from 'works/pages/supplier/admin-settings/supplier-admin-vertical-nav-menu';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import { PageSteps } from '@common/components';
import { MembersAddMultipleUpload } from './members-add-multiple-upload';
import { MembersAddMultipleReview } from './members-add-multiple-review';
import { createMessage } from 'works/legacy/lib/message-helper';
import { ImportMember } from 'works/graphql/validate-works-membership-import';
import { Button } from 'antd';
import { RcFile } from 'antd/lib/upload';
import {
  ImportWorksMembership,
  ImportWorksMembershipResponse,
  ImportWorksMembershipVariables,
} from 'works/graphql/import-works-membership';
import { useMutation, useNavigate } from '@common/hooks';
import { CSVError } from 'works/legacy/lib/csv_error';
import { useCurrentEntity } from '@identity/hooks';

function SuccessMessage({
  membersImported,
}: {
  membersImported: ImportMember[];
}) {
  const isPlural = membersImported.length;

  function suffix() {
    const hasAddition = membersImported.some((member) => !member.isUpdate);
    const hasEdition = membersImported.some((member) => member.isUpdate);

    if (hasAddition && hasEdition) {
      return 'added or updated';
    }

    if (hasAddition) {
      return 'added';
    }

    return 'updated';
  }

  return (
    <>
      A total of {membersImported.length} users {isPlural ? 'were' : 'was'}{' '}
      <strong>{suffix()}</strong>
    </>
  );
}

export function MembersAddMultipleSteps() {
  const entity = useCurrentEntity();
  const navigate = useNavigate();
  const isOrganization = entity.entityType === 'Organization';
  const [importMembers, setImportMembers] = useState<ImportMember[]>([]);
  const [csvFile, setCsvFile] = useState<RcFile | null>(null);
  const [errors, setErrors] = useState<CSVError[]>([]);
  const [forwardToLocationRoute, setForwardToLocationRoute] = useState(false);
  const [isCsvLoading, setIsCsvLoading] = useState(false);

  const errorMessage = () =>
    createMessage('error', 'Something went wrong. Try again later.');

  useSetBreadcrumbs([
    {
      name: 'Admin',
      path: isOrganization
        ? ORGANIZATION_ADMIN_MENU.HOME.ROUTE
        : SUPPLIER_ADMIN_MENU.HOME.ROUTE,
    },
    {
      name: 'Users',
      path: isOrganization
        ? ORGANIZATION_ADMIN_MENU.USERS.ROUTE
        : SUPPLIER_ADMIN_MENU.USERS.ROUTE,
    },
    {
      name: 'Upload',
      path: `${
        isOrganization
          ? ORGANIZATION_ADMIN_MENU.USERS.ROUTE
          : SUPPLIER_ADMIN_MENU.USERS.ROUTE
      }/upload`,
    },
  ]);

  const [save, { loading: saveLoading }] = useMutation<
    ImportWorksMembershipResponse,
    ImportWorksMembershipVariables
  >(ImportWorksMembership, {
    onCompleted(data) {
      const importation = data.importWorksMembership;

      if (importation.errors.length) {
        setErrors(importation.errors);
        return;
      }

      createMessage(
        'success',
        <SuccessMessage membersImported={importMembers} />
      );

      if (forwardToLocationRoute) {
        navigate(`${ORGANIZATION_ADMIN_MENU.USERS.ROUTE}/update-locations`, {
          state: {
            memberIds: importation.uploadedMembers,
          },
        });
        return;
      }

      navigate(
        isOrganization
          ? ORGANIZATION_ADMIN_MENU.USERS.ROUTE
          : SUPPLIER_ADMIN_MENU.USERS.ROUTE,
        {
          state: {
            updatedMemberIds: importation.uploadedMembers,
          },
        }
      );
    },
    onError: errorMessage,
  });

  function handleSave() {
    if (!csvFile) {
      errorMessage();
      return;
    }

    save({
      variables: {
        input: {
          file: csvFile,
        },
      },
    });
  }

  function handleConfirm() {
    setForwardToLocationRoute(false);
    handleSave();
  }

  return (
    <>
      <PageSteps
        onCancel={() => {
          navigate(
            isOrganization
              ? ORGANIZATION_ADMIN_MENU.USERS.ROUTE
              : SUPPLIER_ADMIN_MENU.USERS.ROUTE
          );
        }}
        steps={[
          {
            title: 'Upload File',
            page: (
              <MembersAddMultipleUpload
                isOrganization={isOrganization}
                setCsvFile={setCsvFile}
                csvFile={csvFile}
                onSuccess={setImportMembers}
                onErrors={setErrors}
                onLoading={setIsCsvLoading}
              />
            ),
            forwardButtonDisabled: !importMembers.length && !errors.length,
            forwardButtonLoading: isCsvLoading,
          },
          {
            title: 'Review Import',
            page: (
              <MembersAddMultipleReview
                errors={errors}
                importMembers={importMembers}
              />
            ),
            customForwardAction() {
              if (errors.length) {
                return <></>;
              }

              function handleAddLocationConfirm() {
                setForwardToLocationRoute(true);
                handleSave();
              }

              return (
                <>
                  <Button
                    onClick={handleConfirm}
                    loading={saveLoading}
                    type="primary"
                    size="large"
                  >
                    Upload Users
                  </Button>
                  {isOrganization && (
                    <Button
                      onClick={handleAddLocationConfirm}
                      loading={saveLoading}
                      type="primary"
                      size="large"
                    >
                      Confirm & Add Locations
                    </Button>
                  )}
                </>
              );
            },
          },
        ]}
        onConfirm={() => {}}
      />
    </>
  );
}
